/** Pure helpers for the Organizations tab (tests/organizations.test.ts). */
import type { ClientSummary, FundedMember, LobbiedItem, MoneyBreakdown, PositionBasis } from "./types";

export const total = (m: MoneyBreakdown) => m.company + m.employees + m.lobbyists;

/** "All sources" vs "Company only" (company + employees, without lobbyists' own giving). */
export type MoneyView = "all" | "company";
export const amountFor = (m: MoneyBreakdown, view: MoneyView) => (view === "all" ? total(m) : m.company + m.employees);

/** Share of a client's total that came from its lobbyists (Airbnb ≈ 100%, CAA ≈ 59%). */
export const lobbyistShare = (m: MoneyBreakdown) => (total(m) ? m.lobbyists / total(m) : 0);

/** Show the overlap note whenever lobbyist money is part of a total on screen. */
export const needsOverlapNote = (m: MoneyBreakdown, view: MoneyView) => view === "all" && m.lobbyists > 0;

export type ClientSort = "legislation" | "filings" | "money" | "az";
export function sortClients(rows: ClientSummary[], by: ClientSort, view: MoneyView = "all") {
  const key: Record<ClientSort, (c: ClientSummary) => number | string> = {
    legislation: (c) => -c.legislationCount, filings: (c) => -c.filings, money: (c) => -amountFor(c.money, view), az: (c) => c.name.toLowerCase(),
  };
  return rows.map((r, i) => ({ r, i })).sort((a, b) => {
    const ka = key[by](a.r), kb = key[by](b.r);
    return (ka < kb ? -1 : ka > kb ? 1 : 0) || a.i - b.i;
  }).map((x) => x.r);
}

/** Chip text for a position. Never shows a bare "Opposes" unless a filing stated it. */
export function positionLabel(position: "support" | "oppose" | null, basis: PositionBasis): { text: string; tone: "positive" | "negative" | "neutral" } {
  if (basis === "unclear" || !position) return { text: "Position unclear", tone: "neutral" };
  const word = position === "oppose" ? "Opposes" : "Supports";
  return { text: basis === "inferred" ? `${word} · inferred` : word, tone: position === "oppose" ? "negative" : "positive" };
}

/** "Positions we can see" card on the Votes & forecast tab. */
export function positionCounts(items: LobbiedItem[]) {
  return {
    stated: items.filter((i) => i.basis === "stated").length,
    inferred: items.filter((i) => i.basis === "inferred").length,
    unclear: items.filter((i) => i.basis === "unclear").length,
  };
}

/** Money-linked votes need a STATED position; inferred positions only feed forecasts (labelled). */
export const canComputeMoneyLinked = (basis: PositionBasis) => basis === "stated";

/** "Your issues" chip: first of the client's topics the viewer follows. */
export const yourIssue = (c: ClientSummary, followedTopics: string[]) =>
  c.topics.find((t) => followedTopics.some((f) => f.toLowerCase() === t.toLowerCase()));

export const fundedSorted = (rows: FundedMember[], view: MoneyView) =>
  [...rows].sort((a, b) => (view === "all" ? b.amount - a.amount : b.companyOnly - a.companyOnly));

export const usd = (n: number) => (n ? `$${n.toLocaleString("en-US")}` : "—");
export const usdShort = (n: number) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `$${Math.round(n / 1e3)}K` : usd(n));

// ---------- display helpers (screenshots/56–66) ----------
const utc = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", ...o });
export const fullDate = (iso: string) => utc(iso, { month: "short", day: "numeric", year: "numeric" });   // "Jun 30, 2026"
export const monthDay = (iso: string) => utc(iso, { month: "short", day: "numeric" });                    // "Oct 2"

/** "Q2 2026 · APR 1 – JUN 30" for a quarter ending on `iso`. */
export function quarterLabel(iso: string) {
  const end = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  const q = Math.floor(end.getUTCMonth() / 3) + 1;
  const start = new Date(Date.UTC(end.getUTCFullYear(), (q - 1) * 3, 1, 12));
  const md = (d: Date) => d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" }).toUpperCase();
  return `Q${q} ${end.getUTCFullYear()} · ${md(start)} – ${md(end)}`;
}

/** Filing text as filed, but sentence case when the filer typed in all caps. */
const ACRONYMS = /\b(rlto|pro|dob|doh|ada|adu|tif|cdot|bacp|str|llc|cha|cps|cta|dpd|ordinance [0o]\d{4}-\d{7})\b/g;
export const asFiled = (s: string) => (s === s.toUpperCase() ? (s[0] + s.slice(1).toLowerCase()).replace(ACRONYMS, (m) => m.toUpperCase().replace(/^ORDINANCE/, "ordinance")) : s);

/** "Trade association" → "association" ("…to see what the association itself gave"). */
export const kindNoun = (kind: string) => (kind ? kind.trim().split(/\s+/).at(-1)!.toLowerCase() : "organization");

/** "A, B and C" */
export const andList = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}` : xs[0] ?? "");

/** Lobbyist overlap peek (screenshots/66 #2): "Also counted under 61 other clients, including …". */
export function overlapPeek(l: { clients: number; otherClients?: string[]; clientNames?: string[] }, here: string) {
  const others = (l.otherClients ?? l.clientNames ?? []).filter((n) => n.toLowerCase() !== here.toLowerCase());
  const n = Math.max(0, l.clients - 1);
  if (!n) return "";
  const pick = others.slice(0, 3);
  return `Also counted under ${n} other client${n === 1 ? "" : "s"}${pick.length ? `, including ${andList(pick)}` : ""}.`;
}

/** Lobbyists tab side card (screenshots/63): why a multi-client lobbyist's money matters here. */
export function whyThisMatters(m: MoneyBreakdown, lob: { clients: number; otherClients: string[] }, short: string) {
  const lead = lobbyistShare(m) > 0.5 ? "Most of" : "Part of";
  const first = lob.otherClients[0];
  const rest = lob.clients - 2;
  return `${lead} this client's money comes from a lobbyist with ${lob.clients} clients. When you see ${short}'s total, remember it includes giving that also appears under ${first ? `${first}${rest > 0 ? ` and ${rest} others` : ""}` : `${lob.clients - 1} others`}.`;
}

/** Money each bar shows in the current view (bars keep their order and re-scale in place, screenshots/66 #1). */
export const memberAmount = (r: FundedMember, view: MoneyView) => (view === "all" ? r.amount : r.companyOnly);

/** A client's page slug from its name, as scripts/money_links.py makes it (slug(entity_key(name))). Lets other sections
 *  link a donor or lobbying client to /dashboard/organizations/<slug> (and People · Money read ?org=<slug>). */
const ENTITY_SUFFIX = /\b(llc|l l c|inc|incorporated|corp|corporation|co|company|ltd|lp|llp|pc|pllc|na|the|of|and|dba)\b/g;
export const clientSlug = (name: string) =>
  // As money_links.py: slug(entity_key(name)), where words() drops accents, apostrophes and periods ("Amazon.Com" → "amazoncom").
  name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/['’.]/g, "").replace(/[^a-z0-9]+/g, " ").replace(ENTITY_SUFFIX, " ").trim().replace(/\s+/g, "-").slice(0, 80);
